'use client'

import { usePathname } from 'next/navigation'
import { ArrowUpRight, ChevronRight, Mail, MapPin, Navigation, Phone } from 'lucide-react'
import { navigateUrl } from '@/utils/client-request'

type CardItem = {
  eyebrow: string
  value: string
  sub?: string
  href: string
  cta: string
  icon: React.ReactNode
  external?: boolean
}

export default function Contact({ vendor }: { vendor?: any }) {
  const pathname = usePathname()
  const vendorName = vendor?.name || 'Дэлгүүр'

  const items: CardItem[] = []
  if (vendor?.phone) {
    items.push({
      eyebrow: 'Утас',
      value: vendor.phone,
      sub: 'Худалдагчтай шууд ярих',
      href: `tel:${vendor.phone}`,
      cta: 'Залгах',
      icon: <Phone className="h-4 w-4" />,
    })
  }
  if (vendor?.email) {
    items.push({
      eyebrow: 'И-мэйл',
      value: vendor.email,
      sub: 'Ажлын өдөр 24 цагт хариулна',
      href: `mailto:${vendor.email}`,
      cta: 'Мэйл илгээх',
      icon: <Mail className="h-4 w-4" />,
    })
  }
  if (vendor?.address) {
    items.push({
      eyebrow: 'Хаяг',
      value: vendor.address,
      sub: vendor?.addressLandmark || 'Биечлэн ирэх бол',
      href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendor.address)}`,
      cta: 'Газрын зураг',
      icon: <MapPin className="h-4 w-4" />,
      external: true,
    })
  }

  return (
    <div>
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-10" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="hidden md:flex items-center gap-1.5 text-[12px] mb-8" style={{ color: 'rgba(255,255,255,0.55)' }}>
          <a href={navigateUrl('/', pathname)} className="hover:text-white transition-colors">Нүүр</a>
          <ChevronRight className="h-3 w-3" style={{ opacity: 0.5 }} />
          <span className="text-white font-medium">Холбоо барих</span>
        </div>

        <h1
          className="mt-3 font-semibold tracking-[-0.025em] leading-[0.95] text-white max-w-3xl"
          style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(36px, 7vw, 64px)' }}
        >
          Бид сонсож байна.
        </h1>
        <p className="mt-5 text-[15px] sm:text-[16px] leading-7 max-w-xl" style={{ color: 'rgba(255,255,255,0.6)' }}>
          {vendorName} дэлгүүрийн захиалга, бүтээгдэхүүн, хамтын ажиллагааны талаарх мэдээллийг эндээс аваарай.
        </p>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-20">
        {items.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((it) => (
              <a
                key={it.eyebrow}
                href={it.href}
                target={it.external ? '_blank' : undefined}
                rel={it.external ? 'noopener noreferrer' : undefined}
                className="group rounded-2xl p-6 sm:p-7 flex flex-col cursor-pointer transition-colors hover:bg-white/5 min-h-55"
                style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="flex items-start justify-between">
                  <div
                    className="w-10 h-10 rounded-full inline-flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
                  >
                    {it.icon}
                  </div>
                  <div
                    className="w-8 h-8 rounded-full inline-flex items-center justify-center transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
                  >
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </div>
                <div className="mt-auto pt-7">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>{it.eyebrow}</div>
                  <div
                    className="mt-2 text-[16px] sm:text-[18px] font-medium text-white leading-snug wrap-break-word"
                    style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}
                  >
                    {it.value}
                  </div>
                  {it.sub && (
                    <div className="mt-1.5 inline-flex items-center gap-1.5 text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
                      {it.eyebrow === 'Хаяг' && vendor?.addressLandmark && it.sub === vendor.addressLandmark && (
                        <Navigation className="h-3 w-3" />
                      )}
                      {it.sub}
                    </div>
                  )}
                </div>
                <div className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-medium" style={{ color: 'rgba(255,255,255,0.85)' }}>
                  {it.cta}
                  <ArrowUpRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-24" style={{ color: 'rgba(255,255,255,0.5)' }}>
            <Mail className="h-8 w-8" />
            <div className="text-[14px]">Холбоо барих мэдээлэл одоогоор алга</div>
          </div>
        )}
      </div>
    </div>
  )
}
